import { Rect, Text } from "react-konva";
import { useMemo } from "react";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import type { ShirtData, Surface } from "../model/types";

type PrintableAreaProps = {
  surface: Surface;
  shirtData: ShirtData;
  placement?: PlacementFormPosition | undefined;
  variant?: "default" | "drag-preview" | "hovered";
};

const PLACEMENT_LABELS: Record<PlacementFormPosition, string> = {
  front: "Front Center",
  frontLeftChest: "Left Chest",
  back: "Back Center"
};

export function PrintableArea({
  surface,
  shirtData,
  placement,
  variant = "default"
}: PrintableAreaProps) {
  const { isDesktop } = useResponsive();
  const isDragPreview = variant === "drag-preview";
  const isHovered = variant === "hovered";

  // Calculate printable area coordinates based on surface bounding box and shirt positioning
  const areaData = useMemo(() => {
    const { boundingBox } = surface;
    const { scale, offsetX, offsetY } = shirtData;

    return {
      left: boundingBox.left * scale + offsetX,
      top: boundingBox.top * scale + offsetY,
      width: boundingBox.width * scale,
      height: boundingBox.height * scale
    };
  }, [surface, shirtData]);

  const styles = useMemo(() => {
    if (isHovered) {
      return {
        stroke: surface.isDark ? "rgba(255, 255, 255, 1)" : "rgb(0, 0, 0, 0.8)",
        strokeWidth: 2,
        cornerRadius: 4,
        fill: "rgba(255, 255, 255, 0.3)",
        opacity: 1
      };
    }
    if (isDragPreview) {
      return {
        stroke: "rgba(236, 72, 153, 0.6)",
        strokeWidth: 2,
        cornerRadius: 4,
        dash: [4, 2],
        fill: "rgba(236, 72, 153, 0.1)",
        opacity: 0.6
      };
    }
    return {
      stroke: "#EC4899",
      strokeWidth: 2,
      cornerRadius: 4,
      dash: [4, 2],
      fill: "transparent",
      opacity: 1
    };
  }, [isHovered, isDragPreview, surface.isDark]);

  return (
    <>
      <Rect
        x={areaData.left}
        y={areaData.top}
        width={areaData.width}
        height={areaData.height}
        listening={false}
        {...styles}
      />
      {placement && (
        <Text
          x={areaData.left + (isDesktop ? 6 : 4)}
          y={areaData.top + (isDesktop ? 6 : 4)}
          text={PLACEMENT_LABELS[placement]}
          fontSize={isDesktop ? 7 : 6}
          fill={surface.isDark ? "white" : "black"}
          align="center"
          listening={false}
        />
      )}
    </>
  );
}
